import { useState, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CampaignWizard } from '@/components/CampaignWizard';
import { UnifiedCampaignCard } from '@/components/UnifiedCampaignCard';
import { CampaignEditDialog } from '@/components/CampaignEditDialog';
import { CampaignReportModal } from '@/components/CampaignReportModal';
import { HandRaisePreflightDialog } from '@/components/HandRaisePreflightDialog';
import { TrainingVideoModal } from '@/components/TrainingVideoModal';
import { useLocationTrainingVideo } from '@/hooks/useLocationTrainingVideo';
import { useUnifiedCampaigns } from '@/hooks/useUnifiedCampaigns';
import { useCampaignCallStats } from '@/hooks/useCampaignCallStats';
import { useToast } from '@/hooks/use-toast';
import { CampaignWizardData, UnifiedCampaign } from '@/types/unifiedCampaign';
import { supabase } from '@/integrations/supabase/client';
import {
  Search,
  Filter,
  BarChart3,
  Users,
  Target,
  TrendingUp,
  Plus,
  Pause,
  PlayCircle,
} from 'lucide-react';

export default function Campaigns() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [showWizard, setShowWizard] = useState(false);
  const [showPreflight, setShowPreflight] = useState(false);
  const [pendingCampaignId, setPendingCampaignId] = useState<string | null>(
    null,
  );
  const [preflightContext] = useState<any>(null);
  const [editingCampaignId, setEditingCampaignId] = useState<string | null>(
    null,
  );
  const [reportCampaign, setReportCampaign] = useState<UnifiedCampaign | null>(
    null,
  );

  const {
    campaigns,
    loading,
    createCampaign,
    updateCampaign,
    updateCampaignStatus,
    cloneCampaign,
    restartCampaign,
    deleteCampaign,
    getCampaignStats,
    provisionInboundAssistant,
  } = useUnifiedCampaigns();

  const { toast } = useToast();
  const { hasVideo, displayTitle } = useLocationTrainingVideo('campaigns');

  const stats = getCampaignStats();

  // Memoize campaign IDs to prevent unnecessary re-renders and data fetches
  const allCampaignIds = useMemo(() => campaigns.map((c) => c.id), [campaigns]);

  // Fetch campaign call stats once for all campaigns
  const timerange = useMemo(() => ({ days: 30 }), []);
  const { campaignStats, loading: statsLoading } = useCampaignCallStats(
    allCampaignIds,
    timerange,
  );

  const filteredCampaigns = campaigns.filter((campaign) => {
    const matchesSearch =
      campaign.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      campaign.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === 'all' || campaign.status === statusFilter;
    const matchesType =
      typeFilter === 'all' || campaign.campaign_type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  const handleCampaignCreated = async (wizardData: CampaignWizardData) => {
    try {
      await createCampaign(wizardData);
      setShowWizard(false);
      toast({
        title: 'Campaign Created',
        description: `${wizardData.name} has been created successfully.`,
      });
    } catch (error) {
      console.error('Failed to create campaign:', error);
      toast({
        title: 'Failed to Create Campaign',
        description:
          error instanceof Error ? error.message : 'Unknown error occurred',
        variant: 'destructive',
      });
    }
  };

  const handleStart = async (campaignId: string) => {
    try {
      // Find the campaign
      const campaign = campaigns.find((c) => c.id === campaignId);
      if (!campaign) throw new Error('Campaign not found');

      const campaignType = campaign.campaign_type || 'outbound';

      console.log(
        `[START_FLOW] Starting ${campaignType} campaign: ${campaign.name}`,
      );

      // Update campaign status to active - this will trigger execution in updateCampaignStatus
      try {
        await updateCampaignStatus(campaignId, 'active');
        console.log('[SUCCESS] Campaign activated:', campaignId);

        // Success toast is handled in updateCampaignStatus for specific campaign types
      } catch (error) {
        console.error('[ERROR] Failed to start campaign:', error);

        // Provide more specific error messages based on error type
        let errorMessage = 'Unknown error occurred';
        if (error instanceof Error) {
          if (error.message.includes('503')) {
            errorMessage =
              'API key service unavailable. Please check your VAPI keys configuration.';
          } else if (
            error.message.includes('402') ||
            error.message.includes('Payment Required')
          ) {
            errorMessage =
              'Insufficient credits. Please check your credit balance.';
          } else if (
            error.message.includes('401') ||
            error.message.includes('Unauthorized')
          ) {
            errorMessage = 'Authentication failed. Please check your API keys.';
          } else if (
            error.message.includes(
              'Edge Function returned a non-2xx status code',
            )
          ) {
            errorMessage =
              'Campaign execution service is experiencing issues. Please try again.';
          } else {
            errorMessage = error.message;
          }
        }

        toast({
          title: 'Campaign Start Failed',
          description: errorMessage,
          variant: 'destructive',
        });
        throw error;
      }
    } catch (error) {
      console.error('Failed to start campaign:', error);
      // Error toast already shown above for updateCampaignStatus failures
      // Only show additional toast for other failures
      if (!(error instanceof Error && error.message !== 'Campaign not found')) {
        toast({
          title: 'Failed to Start Campaign',
          description:
            error instanceof Error ? error.message : 'Unknown error occurred',
          variant: 'destructive',
        });
      }
    }
  };

  const handlePreflightProceed = async (
    resolvedScript: string,
    checksum: string,
    phoneNumber: string,
  ) => {
    if (!pendingCampaignId) return;

    try {
      // Initiate one test call with exact Hand Raise parameters
      const campaign = campaigns.find((c) => c.id === pendingCampaignId);
      if (!campaign) throw new Error('Campaign not found');

      const { data: result, error } = await supabase.functions.invoke(
        'initiate-vapi-call',
        {
          body: {
            campaignId: pendingCampaignId,
            scriptId: campaign.outbound_script_id || campaign.script_id,
            prospectId: 'test-prospect-id',
            phoneNumber: phoneNumber,
            handRaiseMode: true,
            exactSystemPrompt: resolvedScript,
            scriptChecksum: checksum,
            mergeContext: preflightContext,
          },
        },
      );

      if (error) throw error;

      // Check function result
      if (result && !result.ok) {
        toast({
          title: 'Hand Raise Test Failed',
          description: `Error: ${result.error || 'Unknown error'} (Trace: ${
            result.traceId || 'N/A'
          })`,
          variant: 'destructive',
        });
        return;
      }

      toast({
        title: 'Hand Raise Test Call Initiated',
        description: `Call started with checksum ${checksum.substring(
          0,
          8,
        )}... TraceId: ${result?.traceId || 'N/A'}`,
      });

      setPendingCampaignId(null);
    } catch (error) {
      console.error('Hand Raise test call failed:', error);
      toast({
        title: 'Hand Raise Test Failed',
        description:
          error instanceof Error ? error.message : 'Unknown error occurred',
        variant: 'destructive',
      });
    }
  };

  const handlePause = async (campaignId: string) => {
    try {
      await updateCampaignStatus(campaignId, 'paused');
      toast({
        title: 'Campaign Paused',
        description: 'Your campaign has been paused.',
      });
    } catch (error) {
      toast({
        title: 'Failed to Pause Campaign',
        description: 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleStop = async (campaignId: string) => {
    try {
      await updateCampaignStatus(campaignId, 'completed');
      toast({
        title: 'Campaign Stopped',
        description: 'Your campaign has been stopped.',
      });
    } catch (error) {
      toast({
        title: 'Failed to Stop Campaign',
        description: 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (campaignId: string) => {
    try {
      await deleteCampaign(campaignId);
      toast({
        title: 'Campaign Deleted',
        description: 'Campaign has been permanently deleted.',
      });
    } catch (error) {
      toast({
        title: 'Failed to Delete Campaign',
        description: 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleRebuild = async (campaignId: string) => {
    try {
      await provisionInboundAssistant(campaignId);
    } catch (error) {
      console.error('Failed to provision assistant:', error);
    }
  };

  const handleEdit = (campaignId: string) => {
    setEditingCampaignId(campaignId);
  };

  const handleViewReport = (campaignId: string) => {
    const campaign = campaigns.find((c) => c.id === campaignId);
    if (campaign) setReportCampaign(campaign);
  };

  const handleClone = async (campaignId: string) => {
    try {
      await cloneCampaign(campaignId);
      toast({
        title: 'Campaign Cloned',
        description: 'A draft copy has been created.',
      });
    } catch (error) {
      toast({
        title: 'Clone Failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  const handleRestartFresh = async (campaignId: string) => {
    try {
      await updateCampaignStatus(campaignId, 'active');
    } catch (error) {
      toast({
        title: 'Restart Failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  const handleRestartResume = async (campaignId: string) => {
    try {
      await updateCampaignStatus(campaignId, 'active');
    } catch (error) {
      toast({
        title: 'Restart Failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  const handleEditSave = async (
    id: string,
    updates: Partial<UnifiedCampaign>,
  ) => {
    try {
      await updateCampaign(id, updates);
      toast({
        title: 'Campaign Updated',
        description: 'Changes saved successfully.',
      });
    } catch (error) {
      toast({
        title: 'Update Failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
      throw error;
    }
  };

  if (showWizard) {
    return (
      <div className='container mx-auto p-6'>
        <CampaignWizard
          onComplete={handleCampaignCreated}
          onCancel={() => setShowWizard(false)}
        />
      </div>
    );
  }

  return (
    <div className='container mx-auto p-6 space-y-6'>
      {/* Header */}
      <div className='flex flex-col md:flex-row justify-between items-start md:items-center gap-4'>
        <div className='flex items-center gap-4'>
          <div>
            <h1 className='text-3xl font-bold tracking-tight'>Campaigns</h1>
            <p className='text-muted-foreground'>
              Unified campaign management for all your calling needs
            </p>
          </div>
        </div>

        {hasVideo && (
          <div className='mb-6'>
            <TrainingVideoModal
              trigger={
                <Button variant='outline' size='sm'>
                  <PlayCircle className='h-4 w-4 mr-2' />
                  {displayTitle || 'View Training'}
                </Button>
              }
              locationKey='campaigns'
            />
          </div>
        )}
        <div className='flex items-center gap-2'>
          <Button onClick={() => setShowWizard(true)}>
            <Plus className='h-4 w-4 mr-2' />
            Create Campaign
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className='grid grid-cols-1 md:grid-cols-5 gap-4'>
        <Card>
          <CardContent className='flex items-center p-6'>
            <BarChart3 className='h-8 w-8 text-muted-foreground' />
            <div className='ml-4'>
              <p className='text-sm font-medium text-muted-foreground'>Total</p>
              <p className='text-2xl font-bold'>{stats.total}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='flex items-center p-6'>
            <Target className='h-8 w-8 text-green-600' />
            <div className='ml-4'>
              <p className='text-sm font-medium text-muted-foreground'>
                Active
              </p>
              <p className='text-2xl font-bold'>{stats.active}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='flex items-center p-6'>
            <Pause className='h-8 w-8 text-yellow-600' />
            <div className='ml-4'>
              <p className='text-sm font-medium text-muted-foreground'>
                Paused
              </p>
              <p className='text-2xl font-bold'>{stats.paused}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='flex items-center p-6'>
            <TrendingUp className='h-8 w-8 text-blue-600' />
            <div className='ml-4'>
              <p className='text-sm font-medium text-muted-foreground'>
                Completed
              </p>
              <p className='text-2xl font-bold'>{stats.completed}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='flex items-center p-6'>
            <Users className='h-8 w-8 text-purple-600' />
            <div className='ml-4'>
              <p className='text-sm font-medium text-muted-foreground'>Draft</p>
              <p className='text-2xl font-bold'>{stats.draft}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <div className='flex flex-col sm:flex-row gap-4'>
        <div className='relative flex-1'>
          <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4' />
          <Input
            placeholder='Search campaigns...'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className='pl-10'
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className='w-full sm:w-[180px]'>
            <Filter className='h-4 w-4 mr-2' />
            <SelectValue placeholder='Status' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All Status</SelectItem>
            <SelectItem value='draft'>Draft</SelectItem>
            <SelectItem value='active'>Active</SelectItem>
            <SelectItem value='paused'>Paused</SelectItem>
            <SelectItem value='completed'>Completed</SelectItem>
            <SelectItem value='cancelled'>Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className='w-full sm:w-[180px]'>
            <SelectValue placeholder='Type' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All Types</SelectItem>
            <SelectItem value='outbound'>Outbound</SelectItem>
            <SelectItem value='inbound'>Inbound</SelectItem>
            <SelectItem value='outbound_inbound'>Outbound + Inbound</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Campaigns Grid */}
      {loading ? (
        <div className='text-center py-12'>Loading campaigns...</div>
      ) : filteredCampaigns.length === 0 ? (
        <Card>
          <CardContent className='flex flex-col items-center justify-center py-12'>
            <BarChart3 className='h-12 w-12 text-muted-foreground mb-4' />
            <h3 className='text-lg font-semibold mb-2'>No campaigns found</h3>
            <p className='text-muted-foreground text-center mb-4'>
              {campaigns.length === 0
                ? 'Get started by creating your first campaign.'
                : 'Try adjusting your search or filter criteria.'}
            </p>
            {campaigns.length === 0 && (
              <Button onClick={() => setShowWizard(true)}>
                <Plus className='h-4 w-4 mr-2' />
                Create Campaign
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
          {filteredCampaigns.map((campaign) => (
            <UnifiedCampaignCard
              key={campaign.id}
              campaign={campaign}
              campaignStats={campaignStats}
              statsLoading={statsLoading}
              onStart={handleStart}
              onPause={handlePause}
              onStop={handleStop}
              onDelete={handleDelete}
              onEdit={handleEdit}
              onClone={handleClone}
              onRestartFresh={handleRestartFresh}
              onRestartResume={handleRestartResume}
              onRebuild={handleRebuild}
              onViewReport={handleViewReport}
            />
          ))}
        </div>
      )}

      {/* After Action Report Modal */}
      <CampaignReportModal
        campaign={reportCampaign}
        open={reportCampaign !== null}
        onOpenChange={(open) => {
          if (!open) setReportCampaign(null);
        }}
      />

      {/* Campaign Edit Dialog */}
      <CampaignEditDialog
        campaign={campaigns.find((c) => c.id === editingCampaignId) ?? null}
        open={editingCampaignId !== null}
        onOpenChange={(open) => {
          if (!open) setEditingCampaignId(null);
        }}
        onSave={handleEditSave}
        onPersist={updateCampaign}
      />

      {/* Hand Raise Preflight Modal */}
      <HandRaisePreflightDialog
        open={showPreflight}
        onOpenChange={setShowPreflight}
        context={
          preflightContext || {
            userName: 'Agent',
            companyName: 'Company',
            industry: 'business',
            prospect_name: 'Prospect',
          }
        }
        onProceed={handlePreflightProceed}
      />
    </div>
  );
}
